// @flow
import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import type {TerminalContext} from 'docs/types';
import {
  countrySorter,
  countryNameMessages,
} from 'docs/components/contrib/Terminal/countries';
import {
  TerminalBetaFootnote,
  TerminalBetaFootnoteRef,
} from 'docs/components/contrib/Terminal/terminalBetaFootnote';
import {Table, TableHeaderCell, TableCell} from 'docs/components';
import Box from 'sail/Box';
import FlagIcon from 'sail/FlagIcon';
import {TerminalLocationAddressRequirementsPhrase} from 'docs/components/contrib/TerminalLocationAddressRequirementsPhrase/TerminalLocationAddressRequirementsPhrase';

const messages = defineMessages({
  countries: {
    id: 'docsShell.TerminalLocationAddressRequirementsTable.countries',
    description: 'Table header. Countries',
    defaultMessage: 'Countries',
  },
  requiredAddressProperties: {
    id:
      'docsShell.TerminalLocationAddressRequirementsTable.requiredAddressProperties',
    description: 'Table header. Required Address Properties',
    defaultMessage: 'Required Address Properties',
  },
});

type AddressRequirementsByCountry = $PropertyType<
  TerminalContext,
  'address_requirements_by_country',
>;
type AddressRequirements = $Values<AddressRequirementsByCountry>;
type SerializedAddressRequirements = string;
type CompatibilityData = $PropertyType<TerminalContext, 'compatibility_data'>;

type LocationAddressRequirementsTableProps = {|
  addressRequirementsByCountry: AddressRequirementsByCountry,
  compatibilityData: CompatibilityData,
|};

/**
 * Organizes the input Location address requirements by countries that share the same requirements
 *
 * @param {AddressRequirementsByCountry} addressRequirementsByCountry - Hash of country code to address property requirements.
 * @returns {Map<AddressRequirements, Array<string>>} A map of unique AddressRequirements to a
 *  list of country codes with those requirements. To be used to generate the rows of the table.
 *  e.g. [{line1: true, city: true, state: true, postal_code: true}: ["US", "GB"]]
 */
const createRequirementsToCountriesMap = (
  addressRequirementsByCountry: AddressRequirementsByCountry,
  compatibilityData: CompatibilityData,
): Map<AddressRequirements, Array<string>> => {
  const serializedRequirementsToCountries = new Map<
    SerializedAddressRequirements,
    Array<string>,
  >();
  Object.keys(addressRequirementsByCountry)
    // compatibilityData contains the subset of countries the merchant should see
    .filter((country) => !!compatibilityData[country])
    .forEach((country) => {
      // Serialize object for key comparison as string
      const serializedRequirements = JSON.stringify(
        addressRequirementsByCountry[country],
      );
      const existingCountriesForRequirements = serializedRequirementsToCountries.get(
        serializedRequirements,
      );
      if (existingCountriesForRequirements) {
        serializedRequirementsToCountries.set(serializedRequirements, [
          ...existingCountriesForRequirements,
          country,
        ]);
      } else {
        serializedRequirementsToCountries.set(serializedRequirements, [
          country,
        ]);
      }
    });

  return new Map<AddressRequirements, Array<string>>(
    Array.from(serializedRequirementsToCountries, ([k, v]) => [
      JSON.parse(k),
      v,
    ]),
  );
};

const showingBetaCountries = (compatibilityData: CompatibilityData) =>
  Object.keys(compatibilityData).some(
    (country) => compatibilityData[country]?.in_beta,
  );

export const TerminalLocationAddressRequirementsTable = ({
  addressRequirementsByCountry,
  compatibilityData,
}: LocationAddressRequirementsTableProps) => {
  if (
    !addressRequirementsByCountry ||
    Object.keys(addressRequirementsByCountry).length === 0 ||
    !compatibilityData ||
    Object.keys(compatibilityData).length === 0
  ) {
    return null;
  }

  const requirementsToCountries = createRequirementsToCountriesMap(
    addressRequirementsByCountry,
    compatibilityData,
  );

  return (
    <Box>
      <Table type="striped">
        <thead>
          <tr>
            <TableHeaderCell>
              <FormattedMessage {...messages.countries} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.requiredAddressProperties} />
            </TableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {Array.from(requirementsToCountries.entries()).map(
            ([requirements, countries]) => {
              return (
                <tr key={countries.join('-')}>
                  <TableCell>
                    {countries.sort(countrySorter).map((country) => (
                      <Box key={country} flex={{alignItems: 'center'}}>
                        <Box margin={{right: 4}}>
                          {/* $FlowExpectedError FlagIcon is very strongly typed but `country` is typed as any string; it's fine though since it'll only be proper countries (h/t bibek) */}
                          <FlagIcon countryCode={country} />
                        </Box>
                        <Box flex={{alignItems: 'center'}}>
                          <FormattedMessage
                            {...(countryNameMessages[country] ?? '')}
                          />
                          {compatibilityData[country]?.in_beta && (
                            <TerminalBetaFootnoteRef betaCountry />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell>
                    <TerminalLocationAddressRequirementsPhrase
                      addressRequirementsByCountry={
                        addressRequirementsByCountry
                      }
                      // countries will always have at least one element, as the map entry is only created if a country meets the requirements
                      country={countries[0]}
                    />
                  </TableCell>
                </tr>
              );
            },
          )}
        </tbody>
      </Table>

      {showingBetaCountries(compatibilityData) && (
        <TerminalBetaFootnote betaCountry />
      )}
    </Box>
  );
};
